import {
  buildRichTooltip,
  RICH_TOOLTIP_CLASS,
  tooltipField,
  tooltipText,
  tooltipTitle,
} from "../../../../../../../_shared/_logic/rich-tooltips"

export const CUSTOMER_REGISTRY_ACTION_TOOLTIP_CLASS = RICH_TOOLTIP_CLASS

type RegistryTooltipParts = {
  title: string
  action: string
  effect?: string
  hint?: string
  prerequisite?: string
}

const actionTooltip = ({
  title,
  action,
  effect,
  hint,
  prerequisite,
}: RegistryTooltipParts): string =>
  buildRichTooltip([
    tooltipTitle(title),
    tooltipField("Was passiert", action),
    tooltipField("Auswirkung", effect),
    tooltipField("Voraussetzung", prerequisite),
    tooltipText(hint),
  ])

export const customerRegistryActionTooltips = {
  customerImportFreeTrial: actionTooltip({
    title: "Kehrbezirksimport nicht verfügbar",
    action: "Der Kehrbezirksimport ist in einer Free-Trial-Organisation deaktiviert.",
    hint: "Nach dem Wechsel auf einen bezahlten Tarif kann der Import gestartet werden.",
  }),
  importLocked: actionTooltip({
    title: "Kehrbezirk gesperrt",
    action:
      "Während ein Kehrbezirksimport verarbeitet wird, sind Änderungen an Kunden, Liegenschaften, Einheiten und Zuordnungen gesperrt.",
    effect: "So verhindert KehrBlatt widersprüchliche Daten während der Importverarbeitung.",
  }),
  customerImport: actionTooltip({
    title: "Kehrbezirksimport",
    action:
      "Öffnet den Import-Assistenten für Kunden, Liegenschaften, Einheiten und Ansprechpartner.",
    effect:
      "Während des Imports können Kunden, Liegenschaften, Einheiten und deren Zuordnungen nicht geändert werden.",
    hint: "Das Importfenster zeigt den Fortschritt und anschließend das Ergebnis.",
  }),
  propertyAdd: actionTooltip({
    title: "Liegenschaft hinzufügen",
    action:
      "Legt eine neue Liegenschaft an und kann sie direkt mit dem aktuellen Ansprechpartner verknüpfen.",
    effect:
      "Die Liegenschaft steht danach für Aufträge, Einheiten und Kontaktplanung zur Verfügung.",
  }),
  propertySelect: actionTooltip({
    title: "Bestehende Liegenschaft auswählen",
    action: "Verknüpft den aktuellen Ansprechpartner mit einer bereits vorhandenen Liegenschaft.",
    effect: "Es werden keine neuen Stammdaten angelegt; nur die Zuordnung wird ergänzt.",
  }),
  propertyArchive: actionTooltip({
    title: "Liegenschaft archivieren",
    action: "Blendet diese Liegenschaft aus neuen Suchen und Zuordnungen aus.",
    effect:
      "Bestehende Aufträge, Einheiten und Ansprechpartner-Zuordnungen bleiben unverändert erhalten.",
  }),
  propertyRestore: actionTooltip({
    title: "Liegenschaft wiederherstellen",
    action: "Gibt diese Liegenschaft wieder für neue Suchen und Zuordnungen frei.",
  }),
  propertyBatchArchive: actionTooltip({
    title: "Ausgewählte Liegenschaften archivieren",
    action: "Blendet alle ausgewählten Liegenschaften aus neuen Suchen und Zuordnungen aus.",
    effect: "Bestehende Aufträge, Einheiten und Ansprechpartner-Zuordnungen bleiben erhalten.",
  }),
  propertyBatchRestore: actionTooltip({
    title: "Ausgewählte Liegenschaften wiederherstellen",
    action: "Gibt alle ausgewählten Liegenschaften wieder frei.",
  }),
  customerArchive: actionTooltip({
    title: "Ansprechpartner archivieren",
    action: "Blendet den Ansprechpartner aus neuen Suchen und freien Zuordnungen aus.",
    effect: "Bestehende Aufträge, Kontaktpunkte und Zuordnungen bleiben erhalten.",
  }),
  customerRestore: actionTooltip({
    title: "Ansprechpartner wiederherstellen",
    action: "Gibt den Ansprechpartner wieder für neue Suchen und Zuordnungen frei.",
  }),
  customerBatchArchive: actionTooltip({
    title: "Ausgewählte Ansprechpartner archivieren",
    action: "Blendet alle ausgewählten Ansprechpartner aus neuen Suchen und Zuordnungen aus.",
  }),
  customerBatchRestore: actionTooltip({
    title: "Ausgewählte Ansprechpartner wiederherstellen",
    action: "Gibt alle ausgewählten Ansprechpartner wieder frei.",
  }),
  unitAdd: actionTooltip({
    title: "Einheit hinzufügen",
    action: "Legt eine neue Einheit in dieser Liegenschaft an.",
    effect: "Die Einheit kann danach Ansprechpartnern und Aufträgen zugeordnet werden.",
  }),
  unitArchive: actionTooltip({
    title: "Einheit archivieren",
    action: "Blendet diese Einheit aus neuen Suchen und Zuordnungen aus.",
    effect: "Bestehende Aufträge und Ansprechpartner-Zuordnungen bleiben erhalten.",
  }),
  unitRestore: actionTooltip({
    title: "Einheit wiederherstellen",
    action: "Gibt diese Einheit wieder für neue Suchen und Zuordnungen frei.",
  }),
  assignContactsToUnit: actionTooltip({
    title: "Ansprechpartner zuordnen",
    action: "Öffnet die Zuordnung von Ansprechpartnern zu dieser Einheit.",
    effect:
      "Die Zuordnung steuert Rollen, Kontaktplanung und spätere Terminbriefe für diese Einheit.",
  }),
  removeUnitContactAssignment: actionTooltip({
    title: "Zuordnung entfernen",
    action: "Trennt diesen Ansprechpartner von der Einheit.",
    effect:
      "Der Ansprechpartner bleibt erhalten, wird aber für diese Einheit nicht mehr automatisch angesprochen.",
  }),
  contactPointAdd: actionTooltip({
    title: "Kontaktpunkt hinzufügen",
    action: "Legt E-Mail-Adresse, Telefonnummer oder Postadresse für diesen Ansprechpartner an.",
    effect:
      "Neue Kontaktpunkte können für die im Tarif verfügbaren Kundeninformationen genutzt werden.",
  }),
  contactPointEdit: actionTooltip({
    title: "Kontaktpunkt bearbeiten",
    action: "Ändert diesen Kontaktweg.",
    effect:
      "Künftige Kundeninformationen nutzen den aktualisierten Wert. Bereits ausgeführte Nachrichten bleiben historisch unverändert.",
  }),
  contactPointDelete: actionTooltip({
    title: "Kontaktpunkt löschen",
    action: "Löscht diesen Kontaktweg aus KehrBlatt.",
    effect: "Künftige Kundeninformationen können diesen Wert danach nicht mehr verwenden.",
    hint: "Nutzen Sie das, wenn ein Kontaktweg falsch oder nicht mehr gültig ist.",
  }),
} as const

export const buildDeleteContactPointApprovalDescription = (label: string): string =>
  `Kontaktpunkt "${label}" wirklich löschen? Danach kann KehrBlatt diesen Kontaktweg nicht mehr für Kundeninformationen verwenden.`
